import { useFieldErrors } from '../hooks/useFieldErrors';
import { useMemo, useState } from 'react';
import { useResetOnOpen } from '../hooks/useResetOnOpen';
import { useRanchoStore } from '../store/rancho.store';
import { CycleCombobox } from '../components/shared/CycleCombobox';
import { buildApplyInput, type ApplyMode } from './protocol/applyProtocolInput';
import {
  makeStyles, tokens, Spinner, Badge, Text, Dropdown, Option,
  Field, Input, Radio, RadioGroup,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
  
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions, Button,
} from '@fluentui/react-components';
import { AddRegular, CopyRegular, DeleteRegular } from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.store';
import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import { useT } from '../i18n';
import { NumberInput } from '../components/shared/NumberInput';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { useRanchoLotes } from '../hooks/useRanchoLotes';
import { fetchModules, fetchProjects } from '../api/modules.api';
import { parseLoteZoneId } from '../utils/lote-zone-options';
import { useLoteZoneOptions } from '../hooks/useLoteZoneOptions';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { fetchRanchos } from '../api/rancho.api';
import {
  fetchProtocols, createProtocol, updateProtocol, deleteProtocol, applyProtocol, duplicateProtocol, isMarcoType,
  type ProtocolRecord, type ProtocolType, type LegacyProtocolType, type ProtocolApplication, type ProtocolCatalogItem,
  type CreateProtocolInput, type ApplyProtocolInput,
} from '../api/protocol.api';
import { cropLabel } from '../utils/crop-label';
import { MarcoEditorDialog } from './protocol/MarcoEditorDialog';
import { ProtocolImportDialog } from './protocol/ProtocolImportDialog';
import { PageTitle } from '../components/shared/PageTitle';
import { DataTable } from '../components/shared/DataTable';

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  filters: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'flex-end', flexWrap: 'wrap' },
  table:   { width: '100%', overflowX: 'auto' as const },
  th:      { fontWeight: tokens.fontWeightSemibold, padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
  td:      { padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
  hint:    { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  appRow:  { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', marginBottom: tokens.spacingVerticalXS },
  chip:    { fontFamily: 'monospace', fontSize: tokens.fontSizeBase100 },
});

const TYPE_COLOR: Record<ProtocolType, 'success' | 'warning' | 'informative' | 'brand' | 'important'> = {
  QUIMICO:             'warning',
  FERTILIZANTE:        'success',
  AMBOS:               'informative',
  MARCO_FERTILIZACION: 'brand',
  MARCO_PULVERIZACION: 'important',
};

// ─── ProtocolFormDialog (crear / editar) ──────────────────────────────────────

const EMPTY_APPLICATION: ProtocolApplication = { dias: '', productos: [{ name: '', dose: '' }], costoHa: 0 };
const EMPTY_CATALOG_ITEM: ProtocolCatalogItem = { name: '', presentacion: '', precioUnitario: 0, category: 'AGROQUIMICO' };

interface ProtocolFormProps {
  open:     boolean;
  onClose:  () => void;
  initial?: ProtocolRecord | null;
}

function toFormApplications(p?: ProtocolRecord | null): ProtocolApplication[] {
  return p?.aplicaciones?.length ? p.aplicaciones.map(a => ({ ...a, productos: a.productos.map(pr => ({ ...pr })) })) : [{ ...EMPTY_APPLICATION, productos: [{ name: '', dose: '' }] }];
}
function toFormCatalog(p?: ProtocolRecord | null): ProtocolCatalogItem[] {
  return p?.catalogo?.length ? p.catalogo.map(c => ({ ...c })) : [{ ...EMPTY_CATALOG_ITEM }];
}

function ProtocolFormDialog({ open, onClose, initial }: ProtocolFormProps) {
  const t  = useT();
  const pt = t.protocol;
  const qc = useQueryClient();
  const isEdit = !!initial;

  const [crop, setCrop] = useState(initial?.crop ?? '');
  const activeRanchoId = useRanchoStore((st) => st.activeRanchoId);
  const [ranchoId, setRanchoId] = useState(initial?.ranchoId ?? (isEdit ? '' : activeRanchoId ?? ''));
  const [ciclo, setCiclo] = useState(initial?.ciclo ?? '');

  const { data: ranchos = [] } = useQuery({
    queryKey: ['ranchos'],
    queryFn: fetchRanchos,
  });
  const legacyOf = (v?: ProtocolType): LegacyProtocolType => (v && !isMarcoType(v) ? v : 'QUIMICO');
  const [type, setType] = useState<LegacyProtocolType>(legacyOf(initial?.type));
  const [costoQuimicoHa, setCostoQuimicoHa] = useState(initial?.costoQuimicoHa?.toString() ?? '');
  const [costoFertilizanteHa, setCostoFertilizanteHa] = useState(initial?.costoFertilizanteHa?.toString() ?? '');
  const [costoTotalHa, setCostoTotalHa] = useState(initial?.costoTotalHa?.toString() ?? '');
  const [applications, setApplications] = useState<ProtocolApplication[]>(toFormApplications(initial));
  const [catalog, setCatalog] = useState<ProtocolCatalogItem[]>(toFormCatalog(initial));
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  useResetOnOpen(open, () => {
    setCrop(initial?.crop ?? '');
    setRanchoId(initial?.ranchoId ?? (isEdit ? '' : activeRanchoId ?? ''));
    setCiclo(initial?.ciclo ?? '');
    setType(legacyOf(initial?.type));
    setCostoQuimicoHa(initial?.costoQuimicoHa?.toString() ?? '');
    setCostoFertilizanteHa(initial?.costoFertilizanteHa?.toString() ?? '');
    setCostoTotalHa(initial?.costoTotalHa?.toString() ?? '');
    setApplications(toFormApplications(initial));
    setCatalog(toFormCatalog(initial));
    setError(''); fe.clear();
  });

  const typeLabel: Record<LegacyProtocolType, string> = {
    QUIMICO: pt.typeQUIMICO, FERTILIZANTE: pt.typeFERTILIZANTE, AMBOS: pt.typeAMBOS,
  };

  const updateApp = (idx: number, patch: Partial<ProtocolApplication>) =>
    setApplications(apps => apps.map((a, i) => i === idx ? { ...a, ...patch } : a));
  const updateAppProduct = (appIdx: number, prodIdx: number, patch: Partial<ProtocolApplication['productos'][number]>) =>
    setApplications(apps => apps.map((a, i) => i === appIdx
      ? { ...a, productos: a.productos.map((p, j) => j === prodIdx ? { ...p, ...patch } : p) }
      : a));
  const updateCatalogItem = (idx: number, patch: Partial<ProtocolCatalogItem>) =>
    setCatalog(items => items.map((c, i) => i === idx ? { ...c, ...patch } : c));

  const mut = useMutation({
    mutationFn: () => {
      const input: CreateProtocolInput = {
        crop: crop.trim() || undefined,
        ranchoId: ranchoId.trim() || undefined,
        ciclo: ciclo.trim() || undefined,
        type,
        aplicaciones: applications.filter(a => a.dias.trim() && a.productos.some(p => p.name.trim())),
        catalogo: catalog.filter(c => c.name.trim()),
        costoQuimicoHa: costoQuimicoHa ? parseFloat(costoQuimicoHa) : undefined,
        costoFertilizanteHa: costoFertilizanteHa ? parseFloat(costoFertilizanteHa) : undefined,
        costoTotalHa: parseFloat(costoTotalHa) || 0,
      };
      return isEdit ? updateProtocol(initial!.protocolId, input) : createProtocol(input);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['protocols'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? pt.createError),
  });

  const handleSubmit = () => {
    setError('');
    const validApps = applications.filter(a => a.dias.trim() && a.productos.some(p => p.name.trim()));
    const ok = fe.validate({
      type: !type && pt.typeRequired,
      costoTotalHa: (!costoTotalHa || parseFloat(costoTotalHa) <= 0) && pt.costoTotalRequired,
      applications: validApps.length === 0 && pt.applicationsRequired,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '760px' }}>
        <DialogBody>
          <DialogTitle action={
            <TelegramHelpButton
              topic="protocol"
              command="/crear_protocolo"
              commandDesc={t.telegramHelp.protocolCommandDesc}
              voiceExample={t.telegramHelp.protocolVoice}
              voiceDesc={t.telegramHelp.protocolDesc}
            />
          }>{isEdit ? pt.editProtocolTitle : pt.newProtocolTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '8px', maxHeight: '65vh', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={pt.fieldCrop}>
                  <Input value={crop} onChange={(_, d) => setCrop(d.value)} disabled={mut.isPending} />
                </Field>
                <SearchableSelect
                  label={pt.fieldRanchoId}
                  placeholder={t.common.searchRanchoPlaceholder}
                  options={ranchos.map(r => ({ id: r.ranchoId, label: r.name }))}
                  value={ranchoId}
                  onChange={setRanchoId}
                  disabled={mut.isPending}
                  noResultsText={t.common.searchNoResults}
                />
                <Field label={pt.fieldCiclo}>
                  <Input value={ciclo} onChange={(_, d) => setCiclo(d.value)} disabled={mut.isPending} />
                </Field>
              </div>

              <Field label={pt.fieldType} {...fe.props('type')}>
                <Dropdown
                  selectedOptions={[type]}
                  value={typeLabel[type]}
                  onOptionSelect={(_, d) => setType(d.optionValue as LegacyProtocolType)}
                  disabled={mut.isPending}
                >
                  <Option value="QUIMICO">{pt.typeQUIMICO}</Option>
                  <Option value="FERTILIZANTE">{pt.typeFERTILIZANTE}</Option>
                  <Option value="AMBOS">{pt.typeAMBOS}</Option>
                </Dropdown>
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={pt.fieldCostoQuimico}>
                  <NumberInput value={costoQuimicoHa} onValueChange={(v) => setCostoQuimicoHa(v)} disabled={mut.isPending} />
                </Field>
                <Field label={pt.fieldCostoFert}>
                  <NumberInput value={costoFertilizanteHa} onValueChange={(v) => setCostoFertilizanteHa(v)} disabled={mut.isPending} />
                </Field>
                <Field label={pt.fieldCostoTotal} {...fe.props('costoTotalHa')}>
                  <NumberInput value={costoTotalHa} onValueChange={(v) => { setCostoTotalHa(v); fe.clear('costoTotalHa'); }} disabled={mut.isPending} />
                </Field>
              </div>

              <Text weight="semibold" size={300}>{pt.applicationsTitle}</Text>
              {applications.map((app, appIdx) => (
                <div key={appIdx} style={{ border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto', gap: '8px', alignItems: 'end' }}>
                    <Field label={pt.fieldDias} {...(appIdx === 0 ? fe.props('applications') : {})}>
                      <Input value={app.dias} onChange={(_, d) => { updateApp(appIdx, { dias: d.value }); fe.clear('applications'); }} disabled={mut.isPending} />
                    </Field>
                    <Field label={pt.fieldCostoHaApp}>
                      <NumberInput value={String(app.costoHa || '')} onValueChange={(v) => updateApp(appIdx, { costoHa: parseFloat(v) || 0 })} disabled={mut.isPending} />
                    </Field>
                    <Button
                      size="small" appearance="subtle" icon={<DeleteRegular />}
                      disabled={mut.isPending || applications.length === 1}
                      onClick={() => setApplications(apps => apps.filter((_, i) => i !== appIdx))}
                    >
                      {pt.removeApplication}
                    </Button>
                  </div>
                  {app.productos.map((prod, prodIdx) => (
                    <div key={prodIdx} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto', gap: '8px' }}>
                      <Field label={pt.fieldProductName}>
                        <Input value={prod.name} onChange={(_, d) => { updateAppProduct(appIdx, prodIdx, { name: d.value }); fe.clear('applications'); }} disabled={mut.isPending} />
                      </Field>
                      <Field label={pt.fieldDose}>
                        <Input value={prod.dose} onChange={(_, d) => updateAppProduct(appIdx, prodIdx, { dose: d.value })} disabled={mut.isPending} />
                      </Field>
                      <Button
                        size="small" appearance="subtle"
                        disabled={mut.isPending || app.productos.length === 1}
                        onClick={() => updateApp(appIdx, { productos: app.productos.filter((_, j) => j !== prodIdx) })}
                      >
                        {pt.removeProduct}
                      </Button>
                    </div>
                  ))}
                  <Button
                    size="small" icon={<AddRegular />} disabled={mut.isPending}
                    onClick={() => updateApp(appIdx, { productos: [...app.productos, { name: '', dose: '' }] })}
                  >
                    {pt.addProduct}
                  </Button>
                </div>
              ))}
              <Button
                size="small" icon={<AddRegular />} disabled={mut.isPending}
                onClick={() => setApplications(apps => [...apps, { ...EMPTY_APPLICATION, productos: [{ name: '', dose: '' }] }])}
              >
                {pt.addApplication}
              </Button>

              <Text weight="semibold" size={300}>{pt.catalogTitle}</Text>
              {catalog.map((item, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto', gap: '8px', alignItems: 'end' }}>
                  <Field label={pt.fieldProductName}>
                    <Input value={item.name} onChange={(_, d) => updateCatalogItem(idx, { name: d.value })} disabled={mut.isPending} />
                  </Field>
                  <Field label={pt.fieldPresentacion}>
                    <Input value={item.presentacion} onChange={(_, d) => updateCatalogItem(idx, { presentacion: d.value })} disabled={mut.isPending} />
                  </Field>
                  <Field label={pt.fieldPrecioUnitario}>
                    <NumberInput value={String(item.precioUnitario || '')} onValueChange={(v) => updateCatalogItem(idx, { precioUnitario: parseFloat(v) || 0 })} disabled={mut.isPending} />
                  </Field>
                  <Field label={pt.fieldCategory}>
                    <Dropdown
                      selectedOptions={[item.category]}
                      value={item.category}
                      onOptionSelect={(_, d) => updateCatalogItem(idx, { category: d.optionValue as ProtocolCatalogItem['category'] })}
                      disabled={mut.isPending}
                    >
                      <Option value="AGROQUIMICO">{t.inventory.catAgroquimico}</Option>
                      <Option value="FERTILIZANTE">{t.inventory.catFertilizante}</Option>
                    </Dropdown>
                  </Field>
                  <Button
                    size="small" appearance="subtle" icon={<DeleteRegular />}
                    disabled={mut.isPending || catalog.length === 1}
                    onClick={() => setCatalog(items => items.filter((_, i) => i !== idx))}
                  />
                </div>
              ))}
              <Button
                size="small" icon={<AddRegular />} disabled={mut.isPending}
                onClick={() => setCatalog(items => [...items, { ...EMPTY_CATALOG_ITEM }])}
              >
                {pt.addCatalogItem}
              </Button>

              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {pt.create}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── DuplicateProtocolDialog ───────────────────────────────────────────────────

function DuplicateProtocolDialog({ protocol, onClose }: { protocol: ProtocolRecord | null; onClose: () => void }) {
  const t  = useT();
  const pt = t.protocol;
  const qc = useQueryClient();
  const [nombre, setNombre] = useState('');
  const [crop, setCrop]     = useState('');
  const [ciclo, setCiclo]   = useState('');
  const [error, setError]   = useState('');

  useResetOnOpen(!!protocol, () => {
    setNombre(protocol?.nombre ? `${protocol.nombre} (${pt.duplicateCopySuffix})` : '');
    setCrop(protocol?.crop ?? '');
    setCiclo(protocol?.ciclo ?? '');
    setError('');
  });

  const mut = useMutation({
    mutationFn: () => duplicateProtocol(protocol!.protocolId, {
      nombre: nombre.trim() || undefined, crop: crop.trim() || undefined, ciclo: ciclo.trim() || undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['protocols'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? pt.duplicateError),
  });

  return (
    <Dialog open={!!protocol} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface style={{ maxWidth: '480px' }}>
        <DialogBody>
          <DialogTitle>{pt.duplicateTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{pt.duplicateDesc}</Text>
              {protocol && isMarcoType(protocol.type) && (
                <Field label={pt.marcoName}>
                  <Input value={nombre} onChange={(_, d) => setNombre(d.value)} disabled={mut.isPending} />
                </Field>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={pt.fieldCrop}>
                  <Input value={crop} onChange={(_, d) => setCrop(d.value)} disabled={mut.isPending} />
                </Field>
                <Field label={pt.fieldCiclo}>
                  <Input value={ciclo} onChange={(_, d) => setCiclo(d.value)} disabled={mut.isPending} />
                </Field>
              </div>
              {error && <Text role="alert" style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button appearance="primary" onClick={() => mut.mutate()} disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}>
              {pt.duplicateConfirm}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── DeleteProtocolDialog ──────────────────────────────────────────────────────

function DeleteProtocolDialog({ protocol, onClose }: { protocol: ProtocolRecord | null; onClose: () => void }) {
  const t  = useT();
  const pt = t.protocol;
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => deleteProtocol(protocol!.protocolId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['protocols'] }); onClose(); },
  });

  return (
    <Dialog open={!!protocol} onOpenChange={(_, d) => !d.open && onClose()} modalType="alert">
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{pt.deleteTitle}</DialogTitle>
          <DialogContent><Text>{pt.deleteConfirm}</Text></DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              style={{ backgroundColor: tokens.colorStatusDangerBackground3 }}
              onClick={() => mut.mutate()}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {pt.deleteBtn}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── ApplyProtocolDialog ───────────────────────────────────────────────────────

function ApplyProtocolDialog({ protocol, onClose }: { protocol: ProtocolRecord | null; onClose: () => void }) {
  const t  = useT();
  const pt = t.protocol;
  const qc = useQueryClient();

  // Zona del árbol lote > zona > subzona: obligatoria; de ella salen el lote y el módulo.
  const [mode, setMode] = useState<ApplyMode>('budget');
  const [zoneId, setZoneId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [plantingDate, setPlantingDate] = useState('');
  const [hectareas, setHectareas] = useState('');
  const [ciclo, setCiclo] = useState('');
  const [error, setError] = useState('');
  const fe = useFieldErrors();
  const [success, setSuccess] = useState(false);

  const ranchoId = protocol?.ranchoId ?? '';
  const { data: modules = [] } = useQuery({
    queryKey: ['modules-rancho', ranchoId],
    queryFn:  () => fetchModules({ ranchoId }),
    enabled:  !!protocol,
  });
  const { data: projects = [] } = useQuery({
    queryKey: ['projects-rancho', ranchoId],
    queryFn:  () => fetchProjects(ranchoId),
    enabled:  !!protocol,
  });
  const sel = zoneId ? parseLoteZoneId(zoneId, modules) : null;
  const loteId = sel?.loteId ?? '';
  const moduleId = sel?.moduleId ?? '';

  const { lotes } = useRanchoLotes(!!protocol, { value: loteId, reset: () => setZoneId('') }, ranchoId);
  // Solo zonas y subzonas: el plan y las tareas cuelgan de un módulo.
  const allZoneOptions = useLoteZoneOptions(lotes, modules);
  const withTasks = mode === 'tasks';
  const zoneOptions = useMemo(
    () => (withTasks ? allZoneOptions.filter((o) => parseLoteZoneId(o.id, modules)?.moduleId) : allZoneOptions),
    [allZoneOptions, modules, withTasks],
  );
  const reset = () => { setMode('budget'); setZoneId(''); setProjectId(''); setPlantingDate(''); setHectareas(''); setCiclo(''); setError(''); setSuccess(false); fe.clear(); };

  const mut = useMutation({
    mutationFn: () => {
      const input: ApplyProtocolInput = buildApplyInput(mode, {
        loteId, moduleId, projectId, plantingDate, hectareas, ciclo, ranchoId: protocol?.ranchoId,
      });
      return applyProtocol(protocol!.protocolId, input);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['finance'] }); setSuccess(true); },
    onError: (e: any) => setError(e?.response?.data?.error ?? pt.applyError),
  });

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      loteId: withTasks ? !moduleId && pt.zoneRequired : !loteId && pt.loteOrZoneRequired,
      projectId: withTasks && !projectId && pt.projectRequired,
      plantingDate: withTasks && !plantingDate && pt.plantingDateRequired,
      hectareas: (!hectareas || parseFloat(hectareas) <= 0) && pt.hectareasRequired,
      ciclo: !ciclo.trim() && pt.cicloRequired,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <Dialog open={!!protocol} onOpenChange={(_, d) => { if (!d.open) { reset(); onClose(); } }}>
      <DialogSurface style={{ maxWidth: '420px' }}>
        <DialogBody>
          <DialogTitle>{pt.applyTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{pt.applyDesc}</Text>
              <Field label={pt.applyModeLabel}>
                <RadioGroup value={mode} onChange={(_, d) => { setMode(d.value as ApplyMode); setZoneId(''); setCiclo(''); fe.clear(); }} disabled={mut.isPending}>
                  <Radio value="budget" label={pt.applyModeBudget} />
                  <Radio value="tasks" label={pt.applyModeTasks} />
                </RadioGroup>
              </Field>
              {withTasks && <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{pt.applyModeTasksHint}</Text>}
              <SearchableSelect
                label={withTasks ? pt.fieldZone : pt.fieldLoteOrZone}
                placeholder={t.common.searchLotePlaceholder}
                noResultsText={t.common.searchNoResults}
                options={zoneOptions}
                value={zoneId}
                onChange={(id) => { setZoneId(id); setCiclo(''); fe.clear('loteId'); }}
                error={fe.errors.loteId}
                disabled={mut.isPending}
              />
              {withTasks && <Field label={pt.fieldProject} {...fe.props('projectId')}>
                <Dropdown
                  value={projects.find((p) => p.projectId === projectId)?.name ?? ''}
                  selectedOptions={projectId ? [projectId] : []}
                  onOptionSelect={(_, d) => { setProjectId(d.optionValue ?? ''); fe.clear('projectId'); }}
                  disabled={mut.isPending}
                >
                  {projects.map((p) => <Option key={p.projectId} value={p.projectId}>{p.name}</Option>)}
                </Dropdown>
              </Field>}
              {withTasks && <Field label={pt.fieldPlantingDate} {...fe.props('plantingDate')}>
                <Input type="date" value={plantingDate} onChange={(_, d) => { setPlantingDate(d.value); fe.clear('plantingDate'); }} disabled={mut.isPending} />
              </Field>}
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={pt.fieldHectareas} {...fe.props('hectareas')}>
                  <NumberInput value={hectareas} onValueChange={(v) => { setHectareas(v); fe.clear('hectareas'); }} disabled={mut.isPending} />
                </Field>
                <Field label={pt.fieldCiclo} {...fe.props('ciclo')}>
                  <CycleCombobox loteId={loteId} value={ciclo} onChange={(v) => { setCiclo(v); fe.clear('ciclo'); }} disabled={mut.isPending} />
                </Field>
              </div>
              {success && <Text style={{ color: tokens.colorPaletteGreenForeground1, fontSize: '12px' }}>{withTasks ? pt.applySuccessTasks : pt.applySuccess}</Text>}
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { reset(); onClose(); }} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {pt.applyBtn}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export function ProtocolPage() {
  const t = useT();
  const s = useStyles();
  const { user } = useAuthStore();
  const { formatCurrency, formatDate } = useLocaleSettings();
  // Roles con permiso crop:write equivalente (ver ALLOWED_ROLES en protocol.handler.ts)
  const canWrite = user?.roles.some(r => ['AGRONOMIST', 'ADMIN', 'SUPER_ADMIN'].includes(r)) ?? false;

  const [crop, setCrop]         = useState('');
  // Rancho: solo el activo global (TopBar); sin selector local redundante.
  const ranchoId = useRanchoStore((st) => st.activeRanchoId) ?? '';
  const [selected, setSelected] = useState<ProtocolRecord | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ProtocolRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProtocolRecord | null>(null);
  const [applyTarget, setApplyTarget] = useState<ProtocolRecord | null>(null);
  const [marcoOpen, setMarcoOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [marcoTarget, setMarcoTarget] = useState<ProtocolRecord | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<ProtocolRecord | null>(null);

  const { data: protocols = [], isLoading } = useQuery({
    queryKey: ['protocols', crop, ranchoId],
    queryFn:  () => fetchProtocols({
      crop:     crop     || undefined,
      ranchoId: ranchoId || undefined,
    }),
  });

  function typeLabel(type: ProtocolType): string {
    const key = `type${type}` as keyof typeof t.protocol;
    return String(t.protocol[key] ?? type);
  }
  function sourceLabel(src: ProtocolRecord['source']): string {
    const key = `source${src}` as keyof typeof t.protocol;
    return String(t.protocol[key] ?? src);
  }

  return (
    <div className={s.root}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <PageTitle>{t.protocol.title}</PageTitle>
        {canWrite && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button onClick={() => setImportOpen(true)}>
              {t.protocol.importBtn}
            </Button>
            <Button onClick={() => { setMarcoTarget(null); setMarcoOpen(true); }}>
              {t.protocol.newMarco}
            </Button>
            <Button appearance="primary" onClick={() => { setEditTarget(null); setFormOpen(true); }}>
              {t.protocol.newProtocol}
            </Button>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className={s.filters}>
        <Field label={t.protocol.filterCrop}>
          <Input value={crop} onChange={(_, d) => setCrop(d.value)} placeholder="Bitter melon…" />
        </Field>
      </div>

      {/* Tabla */}
      {isLoading && <Spinner size="small" label={t.protocol.loading} />}
      <DataTable<ProtocolRecord>
        rows={isLoading ? [] : protocols}
        rowKey={(p) => p.protocolId}
        emptyMessage={isLoading ? '…' : t.protocol.noData}
        onRowClick={(p) => setSelected(p)}
        columns={[
          { key: 'nombre', header: t.protocol.tableName, render: (p) => p.nombre ?? '—' },
          { key: 'crop', header: t.protocol.tableCrop, render: (p) => (p.crop ? cropLabel(p.crop) : '—') },
          { key: 'ciclo', header: t.protocol.tableCiclo, render: (p) => p.ciclo ?? '—' },
          { key: 'type', header: t.protocol.tableType, render: (p) => <Badge appearance="tint" color={TYPE_COLOR[p.type]}>{typeLabel(p.type)}</Badge> },
          { key: 'cost', header: t.protocol.tableCostHa, render: (p) => (isMarcoType(p.type) ? '—' : formatCurrency(p.costoTotalHa)) },
          { key: 'created', header: t.protocol.tableCreated, render: (p) => formatDate(p.createdAt) },
          { key: 'source', header: t.protocol.tableSource, render: (p) => sourceLabel(p.source) },
          ...(canWrite ? [{ key: 'actions', header: t.common.actions, width: 2.4, render: (p: ProtocolRecord) => (
            // Los botones no deben abrir el detalle de la fila.
            <div style={{ display: 'flex', gap: '4px' }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              {!isMarcoType(p.type) && (
                <Button size="small" appearance="subtle" onClick={() => setApplyTarget(p)}>{t.protocol.applyBtn}</Button>
              )}
              <Button size="small" appearance="subtle" onClick={() => {
                if (isMarcoType(p.type)) { setMarcoTarget(p); setMarcoOpen(true); }
                else { setEditTarget(p); setFormOpen(true); }
              }}>{t.common.edit}</Button>
              <Button size="small" appearance="subtle" icon={<CopyRegular />} onClick={() => setDuplicateTarget(p)}>{t.protocol.duplicateBtn}</Button>
              <Button size="small" appearance="subtle" icon={<DeleteRegular />} style={{ color: tokens.colorStatusDangerForeground1 }} onClick={() => setDeleteTarget(p)} />
            </div>
          ) }] : []),
        ]}
      />

      {/* Dialog detalle */}
      <Dialog open={!!selected} onOpenChange={(_, d) => { if (!d.open) setSelected(null); }}>
        <DialogSurface style={{ maxWidth: selected?.marcoFertilizacion ? 1100 : 680, width: selected?.marcoFertilizacion ? '96vw' : undefined }}>
          <DialogTitle>
            {t.protocol.detailTitle}: {selected?.nombre ?? (selected?.crop ? cropLabel(selected.crop) : '—')} {selected?.ciclo ? `(${selected.ciclo})` : ''}
          </DialogTitle>
          <DialogBody>
            <DialogContent>
              {selected && (
                <>
                  <p>
                    <strong>{t.protocol.tableType}:</strong> {typeLabel(selected.type)} &nbsp;|&nbsp;
                    {!isMarcoType(selected.type) && (<><strong>{t.protocol.tableCostHa}:</strong> {formatCurrency(selected.costoTotalHa)}/ha &nbsp;|&nbsp;</>)}
                    <strong>{t.protocol.tableSource}:</strong> {sourceLabel(selected.source)}
                  </p>

                  {/* Marco de fertilización: matriz tiempos × productos */}
                  {selected.marcoFertilizacion && (
                    <>
                      <h4>{t.protocol.detailMarcoFert}</h4>
                      <div style={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHeader>
                            <TableRow>
                              <TableHeaderCell className={s.th}>{t.protocol.marcoTiempo}</TableHeaderCell>
                              <TableHeaderCell className={s.th}>{t.protocol.marcoDias}</TableHeaderCell>
                              <TableHeaderCell className={s.th}>{t.protocol.marcoTipoFert}</TableHeaderCell>
                              {selected.marcoFertilizacion.columnas.map(c => (
                                <TableHeaderCell key={c.key} className={s.th}>{c.nombre} ({c.unidad})</TableHeaderCell>
                              ))}
                              <TableHeaderCell className={s.th}>{t.protocol.marcoObs}</TableHeaderCell>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selected.marcoFertilizacion.filas.map((f, i) => (
                              <TableRow key={i}>
                                <TableCell className={s.td}>{i + 1}</TableCell>
                                <TableCell className={s.td}>{f.dias}</TableCell>
                                <TableCell className={s.td}>{f.tipo}</TableCell>
                                {selected.marcoFertilizacion!.columnas.map(c => (
                                  <TableCell key={c.key} className={s.td}>{f.cantidades[c.key] ?? ''}</TableCell>
                                ))}
                                <TableCell className={s.td}>{f.observacion ?? ''}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  )}

                  {/* Calendario de aplicaciones (protocolos y marcos de pulverización) */}
                  {!selected.marcoFertilizacion && (<>
                  <h4>{t.protocol.detailApps}</h4>
                  <Table size="small">
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell className={s.th}>{t.protocol.detailDay}</TableHeaderCell>
                        <TableHeaderCell className={s.th}>{t.protocol.detailProducts}</TableHeaderCell>
                        {isMarcoType(selected.type)
                          ? <TableHeaderCell className={s.th}>{t.protocol.marcoObs}</TableHeaderCell>
                          : <TableHeaderCell className={s.th}>{t.protocol.detailCostHa}</TableHeaderCell>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selected.aplicaciones.map((app, i) => (
                        <TableRow key={i}>
                          <TableCell className={s.td}>{app.dias}{app.etapa ? ` · ${app.etapa}` : ''}</TableCell>
                          <TableCell className={s.td}>
                            <div className={s.appRow}>
                              {app.productos.map((pr, j) => (
                                <Badge key={j} appearance="outline" size="small" className={s.chip}>
                                  {pr.name} {pr.dose}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className={s.td}>
                            {isMarcoType(selected.type) ? (app.observacion ?? '') : formatCurrency(app.costoHa)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </>)}

                  {/* Catálogo */}
                  {!isMarcoType(selected.type) && (<>
                  <h4>{t.protocol.detailCatalog}</h4>
                  <Table size="small">
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell className={s.th}>{t.protocol.detailName}</TableHeaderCell>
                        <TableHeaderCell className={s.th}>{t.protocol.detailPresentation}</TableHeaderCell>
                        <TableHeaderCell className={s.th}>{t.protocol.detailPrice}</TableHeaderCell>
                        <TableHeaderCell className={s.th}>{t.protocol.detailCategory}</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selected.catalogo.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell className={s.td}>{item.name}</TableCell>
                          <TableCell className={s.td}>{item.presentacion}</TableCell>
                          <TableCell className={s.td}>{formatCurrency(item.precioUnitario)}</TableCell>
                          <TableCell className={s.td}>{item.category}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </>)}
                </>
              )}
            </DialogContent>
          </DialogBody>
          <DialogActions>
            <Button onClick={() => setSelected(null)}>{t.common.cancel}</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      <ProtocolFormDialog
        key={editTarget?.protocolId ?? 'new-protocol'}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initial={editTarget}
      />
      <MarcoEditorDialog
        key={marcoTarget?.protocolId ?? 'new-marco'}
        open={marcoOpen}
        onClose={() => setMarcoOpen(false)}
        initial={marcoTarget}
      />
      <ProtocolImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
      <DuplicateProtocolDialog protocol={duplicateTarget} onClose={() => setDuplicateTarget(null)} />
      <DeleteProtocolDialog protocol={deleteTarget} onClose={() => setDeleteTarget(null)} />
      <ApplyProtocolDialog protocol={applyTarget} onClose={() => setApplyTarget(null)} />
    </div>
  );
}
